import React, {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import DeliveryListIcon from "../../../../public/images/delivery-list-icon.svg";
import MapIcon from "../../../../public/images/map-icon.svg";
import ManuelDeliveryIcon from "../../../../public/images/manuel-delivery-icon.svg";
import {
    CustomTab,
    CustomTabRow,
    ManuelDeliveryButton,
    SectionIcon,
    SectionTab,
    SectionTabButton,
    SectionTabRow,
    TableCell,
    Wrapper
} from "./DeliveryListBlock.styles";
import {FileSearchOutlined} from "@ant-design/icons";
import {Button, Col, Row, Table} from "antd";
import {useFetch} from "../../../../hooks/useFetch";
import {useStore} from "../../../../hooks/useStore";
import useIsFocused from "../../../../hooks/useIsFocused";
import {ConditionalTooltip} from "../../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {getConstant, isConstantExist, truncateString} from "../../../../utils/helpers";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {deliveryStatuses} from "../../../../constants/constants";
import {CustomButton} from "../../../_defaultComponents/CustomButton/CustomButton";
import {ReceiptPrinter} from "../../../_defaultComponents/ReceiptPrinter/ReceiptPrinter";
import {CustomerApplyButton} from "../../customerOrdersPage/customerApplyButton/CustomerApplyButton";
import {CustomerMap} from "../../../customerMap/CustomerMap";
import {TableWrapper} from "../../../_defaultComponents/TableWrapper/TableWrapper";

const RELOAD_INTERVAL = 1000 * 10; //10 seconds

export const DeliveryListBlock = () => {
    const stores = useStore();
    const history = useHistory();
    const {t, i18n} = useTranslation(["deliveryStatuses", "orderDetail"]);
    const [orderType, setOrderType] = useState("current");
    const [activeTab, setActiveTab] = useState("orders"); // "orders" or "map"
    const isMobile = window.innerWidth < 768;
    const [receiptModalVisible, setReceiptModalVisible] = useState(false);
    const [isReceiptPrinting, setIsReceiptPrinting] = useState(false);
    const receiptRef = useRef();

    const isFocused = useIsFocused();
    const [recall, setRecall] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDate, setOrderDate] = useState(null);
    const recallRef = useRef(recall);
    recallRef.current = recall;

    const nullObjectCheck = obj => {
        return JSON.stringify(obj) === JSON.stringify({});
    };

    const [loading, orderList, _, __] = useFetch({
        fetchFn: stores.orderStore.getOrderList.bind(null, {
            limit: 3,
            skip: 0,
            sortField: {field: "", asc: true},
            companies: [],
            subcompanies: [],
            deliveryStatus: orderType,
            deliveryStatusOverrides: [],
            groups: "all",
            regionGroups: [],
            searchText: ""
        }),
        defaultDataValue: [],
        initialCall: false,
        dependencies: [recall, orderType]
    });

    const [isDedicatedIntegrationLoading, isDedicatedIntegration] = useFetch({
        fetchFn: stores.poolStore.isDedicatedIntegration.bind(),
        dependencies: [],
        initialCall: true
    });

    useEffect(() => {
        setTimeout(() => {
            setRecall(!recallRef.current);
        }, 200);
    }, []);

    useEffect(() => {
        if (isFocused) {
            let timeout = null;

            timeout = setTimeout(() => {
                setRecall(!recallRef.current);
            }, RELOAD_INTERVAL);

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    const columns = [
        {
            title: "Sipariş No",
            dataIndex: "orderNumbers",
            render: (orderNumbers, record) => (
                <TableCell>
                    <ConditionalTooltip
                        condition={true}
                        text={orderNumbers}
                        child={
                            <a href={`/musteri/siparislerim/${record.deliveryId.toString()}`}>
                                {truncateString(orderNumbers, 16)}
                            </a>
                        }
                    />
                </TableCell>
            ),
            responsive: ["md"]
        },
        {
            title: "Tarih",
            dataIndex: "createdAt",
            render: text => <TableCell>{text}</TableCell>,
            responsive: ["md"]
        },
        {
            title: "Şube",
            dataIndex: "subcompanies",
            render: subcompanies => (
                <TableCell>
                    <ConditionalTooltip
                        condition={true}
                        text={subcompanies}
                        child={
                            <div
                                data-testid="subcompanies"
                                style={{maxWidth: "100%", wordBreak: "break-word"}}
                            >
                                {truncateString(subcompanies, 40)}
                            </div>
                        }
                    />
                </TableCell>
            ),
            responsive: ["lg"]
        },
        {
            title: "Müşteri İsmi",
            dataIndex: "customers",
            render: customers => (
                <TableCell>
                    <ConditionalTooltip
                        condition={true}
                        text={customers}
                        child={
                            <div
                                data-testid="customers"
                                style={{maxWidth: "100%", wordBreak: "break-word"}}
                            >
                                {truncateString(customers, 20)}
                            </div>
                        }
                    />
                </TableCell>
            ),
            responsive: ["sm"]
        },
        {
            title: "Seçilen Kurye",
            dataIndex: "requestedBy",
            render: (_, record) => (
                <div>
                    {record.requestedBy ? (
                        <CustomerApplyButton
                            setRecall={setRecall}
                            deliveryId={record._id}
                            courierName={record.requestedBy.fullname}
                            courierId={record.requestedBy._id}
                        />
                    ) : (
                        "-"
                    )}
                </div>
            )
        },
        {
            title: "Kurye",
            dataIndex: "courier",
            render: text => <TableCell>{text}</TableCell>,
            responsive: ["lg"]
        },
        {
            title: "Durum",
            dataIndex: "summary",
            render: (text, delivery) => (
                <TableCell>{renderActiveDeliveryStatusText(delivery)}</TableCell>
            )
        },
        {
            title: "Detay",
            dataIndex: "detail",
            render: (text, delivery) => {
                const orders = delivery?.orders || [];
                const order = orders?.length === 1 ? orders[0] : null;

                return (
                    <>
                        <TableCell>
                            <Row gutter={[8, 8]} align="middle" justify="center">
                                {order ? (
                                    <Col>
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => {
                                                setSelectedOrder(order);
                                                setOrderDate(delivery?.createdAt);
                                                setReceiptModalVisible(true);
                                            }}
                                        >
                                            Fişi görüntüle
                                        </Button>
                                    </Col>
                                ) : null}
                                <Col>
                                    <CustomButton
                                        onClick={() => {
                                            if (orderType === "inQueue") {
                                                return history.push(
                                                    `/musteri/siparislerim?orderType=inQueue`
                                                );
                                            } else {
                                                history.push(
                                                    `/musteri/siparislerim/${delivery?.deliveryId}`
                                                );
                                            }
                                        }}
                                        icon={<FileSearchOutlined />}
                                    />
                                </Col>
                            </Row>
                        </TableCell>
                        <ReceiptPrinter
                            receiptModalVisible={receiptModalVisible}
                            setReceiptModalVisible={setReceiptModalVisible}
                            order={selectedOrder}
                            receiptRef={receiptRef}
                            setIsReceiptPrinting={setIsReceiptPrinting}
                            isReceiptPrinting={isReceiptPrinting}
                            customerCreatedAt={orderDate}
                        />
                    </>
                );
            }
        }
    ];

    const deliveriesMapper = () => {
        if (
            !orderList ||
            nullObjectCheck(orderList) ||
            !Array.isArray(orderList.rows) ||
            orderList?.length === 0
        ) {
            return [];
        }
        const deliveries =
            orderList?.rows?.map((delivery, index) => {
                const orderNumbers = mapOrderNumbers({delivery});
                const subcompanies = mapSubCompany({delivery});
                const customers = delivery.orders.map(order => order.receiverName).join(", ");
                const courier = delivery?.assignedCourier?.fullname || "";
                return {
                    ...delivery,
                    deliveryId: delivery._id,
                    orderNumbers,
                    createdAt: delivery?.createdAt
                        ? moment(delivery.createdAt).format("DD.MM.YYYY HH:mm")
                        : "",
                    subcompanies,
                    customers,
                    courier,
                    status: delivery.status,
                    summary: delivery.summary,
                    requestedBy: delivery.requestedBy
                };
            }) || [];

        return deliveries;
    };

    const mapOrderNumbers = ({delivery}) => {
        let orderNumbers = delivery.orders.map(x => x.orderNumber).join(", ");
        if (delivery.orders.length > 1) {
            orderNumbers = delivery.orders.some(order => order?.orderNumber)
                ? delivery.orders
                      .filter(order => {
                          if (stores.meStore.checkRole("isCompanyUser")) {
                              return (
                                  stores.meStore.auth.companies.includes(
                                      order?.pickupInfoCompanyId?.toString()
                                  ) ||
                                  stores.meStore.auth.subcompanies.includes(
                                      order?.pickupInfoId?.toString()
                                  )
                              );
                          }
                          return true;
                      })
                      .map(order => order?.orderNumber)
                      .join(", ")
                : orderNumbers;
        }
        return orderNumbers;
    };

    const mapSubCompany = ({delivery}) => {
        let subCompanyName = delivery.pickupInfos[0].name;
        if (delivery.orders.length > 1) {
            subCompanyName = delivery.orders.some(order => order?.pickupInfoName)
                ? delivery.orders
                      .filter(order => {
                          if (stores.meStore.checkRole("isCompanyUser")) {
                              return (
                                  stores.meStore.auth.companies.includes(
                                      order?.pickupInfoCompanyId?.toString()
                                  ) ||
                                  stores.meStore.auth.subcompanies.includes(
                                      order?.pickupInfoId?.toString()
                                  )
                              );
                          }
                          return true;
                      })
                      .map(order => order?.pickupInfoName)
                      .join(", ")
                : subCompanyName;
        }
        return subCompanyName;
    };

    const calculateMultiOrderStats = delivery => {
        let totalOrdersCount = delivery?.orders?.length || 0;
        let deliveredOrdersCount = 0;
        let pickedUpOrdersCount = 0;
        if (["current", "burningOrders"].includes("current") && delivery?.orders.length > 1) {
            delivery?.orders?.forEach(order => {
                const hasPickedUp = delivery?.pickupInfos?.some(
                    pI =>
                        pI._id.toString() === order.pickupInfoId.toString() && pI.state === "picked"
                );
                if (hasPickedUp) {
                    pickedUpOrdersCount++;
                }

                if (order.state === "delivered") {
                    deliveredOrdersCount++;
                }
            });
        }
        return {
            totalOrdersCount,
            deliveredOrdersCount,
            pickedUpOrdersCount
        };
    };
    const renderActiveDeliveryStatusText = delivery => {
        let content = "-";

        const {
            totalOrdersCount,
            deliveredOrdersCount,
            pickedUpOrdersCount
        } = calculateMultiOrderStats(delivery);

        if (pickedUpOrdersCount > 0 || deliveredOrdersCount > 0) {
            content = (() => (
                <>
                    {t("mergedOrdersStatus.picked", {
                        ns: "orderDetail",
                        pickedUpCount: pickedUpOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                    ,
                    <br />
                    {t("mergedOrdersStatus.delivered", {
                        ns: "orderDetail",
                        deliveredCount: deliveredOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                </>
            ))();
        } else if (
            delivery.deliveryType === "productReturn" &&
            delivery.deliveryState === "returned"
        ) {
            content = t("delivered.name", {ns: "deliveryStatuses"});
        } else if (delivery.deliveryState === "onDeliveryAddress") {
            const endDate = delivery?.steps?.slice(-1)[0]?.endDate;
            const stepIndex =
                endDate && ["delivered", "returned"].includes(delivery?.deliveryState)
                    ? delivery.currentStepIndex + 1
                    : delivery.currentStepIndex - 1;

            const currentStep = delivery.steps[stepIndex];

            if (Boolean(currentStep.trial?.startDate) && !Boolean(currentStep.trial?.endDate)) {
                content = t("onTrial.name", {ns: "deliveryStatuses"});
            } else {
                content = t(
                    `${getConstant(deliveryStatuses, delivery.deliveryState).translation_key}.name`,
                    {
                        ns: "deliveryStatuses"
                    }
                );
            }
        } else if (delivery.deliveryState === "canceled" && delivery.merged == true) {
            content = t("mergedWithAnotherDelivery", {ns: "orderDetail"});
            if (delivery.mergedAt) {
                const deliveryLink = (
                    <a data-testid="mergedDeliveryLink" href={`/orders/${delivery.mergedAt}`} />
                );

                content = (
                    <Trans
                        i18nKey={"mergedDeliveryInfo"}
                        t={t}
                        ns="orderDetail"
                        components={{mergedDeliveryLink: deliveryLink}}
                    />
                );
            }
        } else if (isConstantExist(deliveryStatuses, delivery.deliveryState)) {
            content = t(
                `${getConstant(deliveryStatuses, delivery.deliveryState).translation_key}.name`,
                {
                    ns: "deliveryStatuses"
                }
            );
        }
        return <div data-testid="deliveryStatuses">{content}</div>;
    };

    const changeOrderType = orderType => {
        setOrderType(orderType);
    };

    return (
        <Wrapper>
            <SectionTabRow>
                <SectionTabButton
                    active={activeTab === "orders"}
                    onClick={() => setActiveTab("orders")}
                >
                    <SectionIcon src={DeliveryListIcon} />
                    Siparişlerim
                </SectionTabButton>
                {isDedicatedIntegration?.isIntegrationTypeDedicated?.isIntegrationTypeDedicated && (
                    <SectionTabButton
                        active={activeTab === "map"}
                        onClick={() => setActiveTab("map")}
                    >
                        <SectionIcon src={MapIcon} />
                        Canlı Harita
                    </SectionTabButton>
                )}
            </SectionTabRow>

            {activeTab === "orders" && (
                <>
                    <CustomTabRow>
                        <CustomTab
                            onClick={() => changeOrderType("current")}
                            active={orderType === "current"}
                        >
                            Aktif Siparişler
                        </CustomTab>
                        <CustomTab
                            onClick={() => changeOrderType("inQueue")}
                            active={orderType === "inQueue"}
                        >
                            Bekleyen Siparişler
                        </CustomTab>
                    </CustomTabRow>

                    <div style={{marginTop: "20px"}}>
                        <TableWrapper
                            rowKey="orderNumbers"
                            loading={loading}
                            columns={columns}
                            dataSource={deliveriesMapper()}
                            pagination={false}
                            scroll={{x: "max-content"}}
                            expandable={
                                window.innerWidth < 768
                                    ? {
                                          expandedRowRender: record => (
                                              <div style={{padding: "8px 0"}}>
                                                  <p>
                                                      <strong>Sipariş No:</strong>{" "}
                                                      {record.orderNumbers}
                                                  </p>
                                                  <p>
                                                      <strong>Tarih:</strong> {record.createdAt}
                                                  </p>
                                                  <p>
                                                      <strong>Şube:</strong> {record.subcompanies}
                                                  </p>
                                                  <p>
                                                      <strong>Müşteri:</strong> {record.customers}
                                                  </p>
                                                  <p>
                                                      <strong>Kurye:</strong> {record.courier}
                                                  </p>
                                              </div>
                                          ),
                                          expandRowByClick: true,
                                          showExpandColumn: true
                                      }
                                    : undefined
                            }
                        />
                    </div>

                    {/* <Row
                        justify="end"
                        style={{
                            width: "100%",
                            marginTop: "10px"
                        }}
                    >
                        <a href={`/musteri/siparislerim`} style={{fontWeight: "bold"}}>
                            <u>Tümünü Gör</u>
                        </a>
                    </Row> */}
                </>
            )}

            {activeTab === "map" && (
                <div>
                    <CustomerMap />
                </div>
            )}

            {/* <ManuelDeliveryButton
                isMobile={isMobile}
                onClick={() => history.push("/musteri/manuel-gonderi-olustur")}
            >
                <img src={ManuelDeliveryIcon} alt="manuel-delivery-icon" />
                Sipariş Oluştur
            </ManuelDeliveryButton> */}
        </Wrapper>
    );
};
